'use client';
import { jsx } from 'react/jsx-runtime';
import { omitThemingProps, defineStyle } from '@chakra-ui/styled-system';
import { dataAttr } from '@chakra-ui/utils/attr';
import { cx } from '@chakra-ui/utils/cx';
import { useState } from 'react';
import { AvatarStylesProvider, AvatarProvider } from './avatar-context.mjs';
import { AvatarIcon } from './avatar-icon.mjs';
import { getInitials } from './get-initials.mjs';
import { useImage } from '../image/use-image.mjs';
import { forwardRef } from '../system/forward-ref.mjs';
import { useMultiStyleConfig } from '../system/use-style-config.mjs';
import { chakra } from '../system/factory.mjs';

const AvatarRoot = forwardRef((props, ref) => {
  const styles = useMultiStyleConfig("Avatar", props);
  const {
    src,
    srcSet,
    name,
    showBorder,
    borderRadius = "full",
    onError,
    onLoad: onLoadProp,
    getInitials: getInitials$1 = getInitials,
    icon = /* @__PURE__ */ jsx(AvatarIcon, {}),
    iconLabel = " avatar",
    loading,
    children,
    borderColor,
    ignoreFallback,
    crossOrigin,
    referrerPolicy,
    ...rest
  } = omitThemingProps(props);
  const [isLoaded, setIsLoaded] = useState(false);
  const status = useImage({
    src,
    onError,
    crossOrigin,
    ignoreFallback,
    onLoad(event) {
      setIsLoaded(true);
      onLoadProp?.(event);
    }
  });
  const avatarStyles = defineStyle({
    borderRadius,
    borderWidth: showBorder ? "2px" : void 0,
    ...styles.root
  });
  if (borderColor) {
    avatarStyles.borderColor = borderColor;
  }
  return /* @__PURE__ */ jsx(AvatarStylesProvider, { value: styles, children: /* @__PURE__ */ jsx(
    AvatarProvider,
    {
      value: {
        src,
        borderRadius,
        crossOrigin,
        status,
        loading,
        srcSet,
        referrerPolicy,
        isLoaded,
        getInitials: getInitials$1,
        icon,
        iconLabel,
        showFallback: !src || !isLoaded,
        name
      },
      children: /* @__PURE__ */ jsx(
        chakra.span,
        {
          ref,
          ...rest,
          className: cx("chakra-avatar", props.className),
          "data-loaded": dataAttr(isLoaded),
          __css: avatarStyles,
          children
        }
      )
    }
  ) });
});
AvatarRoot.displayName = "Avatar";

export { AvatarRoot };
